import { LATER, type Landing } from '@rundown/domain';
import type { DeliveryProjectRequest } from './api';

/**
 * When each part of a project lands — the "delivered in phases, over time"
 * view of a project.
 *
 * A project is a bigger deliverable (UI Revamp, AI Workflows) whose parts ship
 * across many sprints. Where a part stands is its status; when it lands is
 * which of these it is, in this order — which is also the order the parts are
 * listed in, so a project reads top to bottom as its own delivery history and
 * plan:
 *
 *   released    shipped, with the date
 *   running     in the locked sprint, or on the hotfix track
 *   committed   in a sprint still being planned
 *   projected   in the backlog; the ranking lands it in a sprint
 *   later       in the backlog, beyond the planned sprints
 *   unsized     in the backlog with no estimate, so nobody can say when
 */
export type PartPhase = 'released' | 'running' | 'committed' | 'projected' | 'later' | 'unsized';

export interface PartTiming {
  readonly phase: PartPhase;
  /** "Released 11 Sep", "Sprint 13", "Projected Sprint 15", "Later", "Needs analysis". */
  readonly label: string;
  /** Orders parts within a phase: a date or a sprint position. */
  readonly sortKey: string;
}

/**
 * Committed and projected share a rank: both are "coming, in a sprint", and
 * between them the calendar decides — a part projected into Sprint 14 lands
 * before one committed to Sprint 16.
 */
const PHASE_ORDER: Readonly<Record<PartPhase, number>> = {
  released: 0,
  running: 1,
  committed: 2,
  projected: 2,
  later: 3,
  unsized: 4,
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function shortDate(timestamp: string): string {
  const date = new Date(timestamp);
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
}

export interface ProjectionView {
  readonly landing: ReadonlyMap<string, Landing>;
  /** Open sprint key → its name and start date. */
  readonly sprints: ReadonlyMap<string, { readonly name: string; readonly startsOn: string }>;
}

export function partTiming(part: DeliveryProjectRequest, projection: ProjectionView): PartTiming {
  if (part.status === 'released') {
    return {
      phase: 'released',
      label: part.closedAt !== null ? `Released ${shortDate(part.closedAt)}` : 'Released',
      sortKey: part.closedAt ?? part.sprintStartsOn ?? '',
    };
  }
  if (part.isHotfix) return { phase: 'running', label: 'Hotfix track', sortKey: '' };
  if (part.sprintId !== null && part.sprintName !== null) {
    return {
      phase: part.sprintState === 'planning' ? 'committed' : 'running',
      label: part.sprintName,
      sortKey: part.sprintStartsOn ?? '',
    };
  }

  const landing = projection.landing.get(part.id);
  if (landing === undefined || landing.reason === 'unestimated' || part.approxHours <= 0) {
    return { phase: 'unsized', label: 'Needs analysis', sortKey: '' };
  }
  if (landing.sprint === LATER) return { phase: 'later', label: 'Later', sortKey: '' };
  const sprint = projection.sprints.get(landing.sprint);
  return {
    phase: 'projected',
    label: `Projected ${sprint?.name ?? landing.sprint}`,
    sortKey: sprint?.startsOn ?? '',
  };
}

/** Parts in delivery order: what shipped first, then what is running, then what is coming. */
export function inDeliveryOrder<T extends { timing: PartTiming }>(parts: readonly T[]): T[] {
  return parts.toSorted(
    (a, b) =>
      PHASE_ORDER[a.timing.phase] - PHASE_ORDER[b.timing.phase] ||
      a.timing.sortKey.localeCompare(b.timing.sortKey),
  );
}

export interface ProjectProgress {
  readonly released: number;
  readonly total: number;
  readonly deliveredHours: number;
  readonly remainingHours: number;
  /** Parts nobody can place yet — no estimate, or beyond the planned sprints. */
  readonly unscheduled: number;
  /** The furthest sprint any remaining part is in or projected into, if every remaining part has one. */
  readonly lastLanding: string | null;
}

export function projectProgress(
  parts: readonly { part: DeliveryProjectRequest; timing: PartTiming }[],
): ProjectProgress {
  const released = parts.filter((entry) => entry.timing.phase === 'released');
  const remaining = parts.filter((entry) => entry.timing.phase !== 'released');
  const unscheduled = remaining.filter(
    (entry) => entry.timing.phase === 'later' || entry.timing.phase === 'unsized',
  );
  const ordered = inDeliveryOrder(remaining);
  const last = ordered.at(-1);
  return {
    released: released.length,
    total: parts.length,
    deliveredHours: released.reduce((total, entry) => total + entry.part.approxHours, 0),
    remainingHours: remaining.reduce((total, entry) => total + entry.part.approxHours, 0),
    unscheduled: unscheduled.length,
    lastLanding:
      remaining.length > 0 &&
      unscheduled.length === 0 &&
      last !== undefined &&
      last.timing.phase !== 'running'
        ? last.timing.label.replace(/^Projected /, '')
        : null,
  };
}
